import { Template } from "./template";

export type TemplateMetadata = {
  name: Template;
  displayName: string;
  columnType: "single" | "multi";
  enabled: boolean | { resume: boolean; cover: boolean }; // true = enabled for both, or specify per type
  features: {
    themeSupport: boolean; // Color customization and theme support are the same
    photoSupport: boolean;
    layoutCustomization: boolean;
    borderRadius: boolean;
    spacing: boolean;
  };
  layout: {
    mainSections: string[];
    sidebarSections: string[];
    fixedSections: string[]; // Sections that cannot be moved (like basics, summary)
  };
};

export const templateMetadata: Record<Template, TemplateMetadata> = {
  azurill: {
    name: "azurill",
    displayName: "Executive Bold",
    columnType: "multi",
    enabled: true, // Enabled for both resume and cover
    features: {
      themeSupport: true,
      photoSupport: true,
      layoutCustomization: true,
      borderRadius: true,
      spacing: true,
    },
    layout: {
      mainSections: ["experience", "education", "projects", "volunteer"],
      sidebarSections: [
        "skills",
        "languages",
        "awards",
        "certifications",
        "interests",
        "references",
        "publications",
      ],
      fixedSections: ["basics", "summary", "profiles"],
    },
  },
  bronzor: {
    name: "bronzor",
    displayName: "Classic Refined",
    columnType: "single",
    enabled: true, // Enabled for both resume and cover
    features: {
      themeSupport: false,
      photoSupport: true,
      layoutCustomization: true,
      borderRadius: false,
      spacing: true,
    },
    layout: {
      mainSections: [
        "experience",
        "education",
        "skills",
        "projects",
        "languages",
        "awards",
        "certifications",
        "interests",
        "volunteer",
        "references",
        "publications",
      ],
      sidebarSections: [],
      fixedSections: ["basics", "summary", "profiles"],
    },
  },
  chikorita: {
    name: "chikorita",
    displayName: "Creative Edge",
    columnType: "multi",
    enabled: { resume: true, cover: false }, // Cover coming soon
    features: {
      themeSupport: true,
      photoSupport: true,
      layoutCustomization: true,
      borderRadius: true,
      spacing: true,
    },
    layout: {
      mainSections: ["experience", "education", "projects", "volunteer"],
      sidebarSections: [
        "skills",
        "languages",
        "awards",
        "certifications",
        "interests",
        "references",
        "publications",
      ],
      fixedSections: ["basics", "summary", "profiles"],
    },
  },
  ditto: {
    name: "ditto",
    displayName: "Corporate Identity",
    columnType: "multi",
    enabled: { resume: true, cover: false }, // Cover coming soon
    features: {
      themeSupport: true,
      photoSupport: true,
      layoutCustomization: true,
      borderRadius: false,
      spacing: true,
    },
    layout: {
      mainSections: ["experience", "education", "projects", "volunteer"],
      sidebarSections: [
        "skills",
        "languages",
        "awards",
        "certifications",
        "interests",
        "references",
        "publications",
      ],
      fixedSections: ["basics", "summary", "profiles"],
    },
  },
  gengar: {
    name: "gengar",
    displayName: "Business Formal",
    columnType: "multi",
    enabled: { resume: false, cover: false }, // Cover coming soon
    features: {
      themeSupport: false,
      photoSupport: true,
      layoutCustomization: true,
      borderRadius: false,
      spacing: true,
    },
    layout: {
      mainSections: ["experience", "education", "projects", "volunteer"],
      sidebarSections: [
        "skills",
        "languages",
        "awards",
        "certifications",
        "interests",
        "references",
        "publications",
      ],
      fixedSections: ["basics", "summary", "profiles"],
    },
  },
  glalie: {
    name: "glalie",
    displayName: "Leadership Focus",
    columnType: "multi",
    enabled: true, // Enabled for both resume and cover
    features: {
      themeSupport: true,
      photoSupport: true,
      layoutCustomization: true,
      borderRadius: true,
      spacing: true,
    },
    layout: {
      mainSections: ["experience", "education", "projects", "volunteer"],
      sidebarSections: [
        "skills",
        "languages",
        "awards",
        "certifications",
        "interests",
        "references",
        "publications",
      ],
      fixedSections: ["basics", "summary", "profiles"],
    },
  },
  kakuna: {
    name: "kakuna",
    displayName: "Minimalist Pure",
    columnType: "single",
    enabled: true, // Enabled for both resume and cover
    features: {
      themeSupport: false,
      photoSupport: true,
      layoutCustomization: true,
      borderRadius: false,
      spacing: true,
    },
    layout: {
      mainSections: [
        "experience",
        "education",
        "skills",
        "projects",
        "languages",
        "awards",
        "certifications",
        "interests",
        "volunteer",
        "references",
        "publications",
      ],
      sidebarSections: [],
      fixedSections: ["basics", "summary", "profiles"],
    },
  },
  leafish: {
    name: "leafish",
    displayName: "Artistic Vision",
    columnType: "multi",
    enabled: { resume: false, cover: false }, // Cover coming soon
    features: {
      themeSupport: true,
      photoSupport: true,
      layoutCustomization: true,
      borderRadius: true,
      spacing: true,
    },
    layout: {
      mainSections: ["experience", "education", "projects", "volunteer"],
      sidebarSections: [
        "skills",
        "languages",
        "awards",
        "certifications",
        "interests",
        "references",
        "publications",
      ],
      fixedSections: ["basics", "summary", "profiles"],
    },
  },
  nosepass: {
    name: "nosepass",
    displayName: "Scholar Standard",
    columnType: "single",
    enabled: true, // Enabled for both resume and cover
    features: {
      themeSupport: false,
      photoSupport: true,
      layoutCustomization: true,
      borderRadius: false,
      spacing: true,
    },
    layout: {
      mainSections: [
        "experience",
        "education",
        "skills",
        "projects",
        "languages",
        "awards",
        "certifications",
        "interests",
        "volunteer",
        "references",
        "publications",
      ],
      sidebarSections: [],
      fixedSections: ["basics", "summary", "profiles"],
    },
  },
  onyx: {
    name: "onyx",
    displayName: "Heritage Design",
    columnType: "single",
    enabled: true, // Enabled for both resume and cover
    features: {
      themeSupport: false,
      photoSupport: true,
      layoutCustomization: true,
      borderRadius: false,
      spacing: true,
    },
    layout: {
      mainSections: [
        "experience",
        "education",
        "skills",
        "projects",
        "languages",
        "awards",
        "certifications",
        "interests",
        "volunteer",
        "references",
        "publications",
      ],
      sidebarSections: [],
      fixedSections: ["basics", "summary", "profiles"],
    },
  },
  pikachu: {
    name: "pikachu",
    displayName: "Studio Creative",
    columnType: "multi",
    enabled: { resume: false, cover: false }, // Cover coming soon
    features: {
      themeSupport: true,
      photoSupport: true,
      layoutCustomization: true,
      borderRadius: true,
      spacing: true,
    },
    layout: {
      mainSections: ["experience", "education", "projects", "volunteer"],
      sidebarSections: [
        "skills",
        "languages",
        "awards",
        "certifications",
        "interests",
        "references",
        "publications",
      ],
      fixedSections: ["basics", "summary", "profiles"],
    },
  },
  rhyhorn: {
    name: "rhyhorn",
    displayName: "Tech Specialist",
    columnType: "single",
    enabled: { resume: true, cover: false }, // Enabled for both resume and cover
    features: {
      themeSupport: false,
      photoSupport: true,
      layoutCustomization: true,
      borderRadius: false,
      spacing: true,
    },
    layout: {
      mainSections: [
        "experience",
        "education",
        "skills",
        "projects",
        "languages",
        "awards",
        "certifications",
        "interests",
        "volunteer",
        "references",
        "publications",
      ],
      sidebarSections: [],
      fixedSections: ["basics", "summary", "profiles"],
    },
  },
  snorlax: {
    name: "snorlax",
    displayName: "Enterprise Ready",
    columnType: "multi",
    enabled: { resume: true, cover: false },
    features: {
      themeSupport: true,
      photoSupport: true,
      layoutCustomization: true,
      borderRadius: false,
      spacing: true,
    },
    layout: {
      mainSections: ["experience", "education", "projects", "volunteer"],
      sidebarSections: [
        "skills",
        "languages",
        "awards",
        "certifications",
        "interests",
        "references",
        "publications",
      ],
      fixedSections: ["basics", "summary", "profiles"],
    },
  },
  meowth: {
    name: "meowth",
    displayName: "Contemporary Chic",
    columnType: "multi",
    enabled: { resume: true, cover: false },
    features: {
      themeSupport: true,
      photoSupport: true,
      layoutCustomization: true,
      borderRadius: true,
      spacing: true,
    },
    layout: {
      mainSections: ["experience", "education", "projects", "volunteer"],
      sidebarSections: [
        "skills",
        "languages",
        "awards",
        "certifications",
        "interests",
        "references",
        "publications",
      ],
      fixedSections: ["basics", "summary", "profiles"],
    },
  },
  quagsire: {
    name: "quagsire",
    displayName: "Streamlined",
    columnType: "single",
    enabled: { resume: true, cover: false },
    features: {
      themeSupport: false,
      photoSupport: false,
      layoutCustomization: true,
      borderRadius: false,
      spacing: true,
    },
    layout: {
      mainSections: [
        "experience",
        "education",
        "skills",
        "projects",
        "languages",
        "awards",
        "certifications",
        "interests",
        "volunteer",
        "references",
        "publications",
      ],
      sidebarSections: [],
      fixedSections: ["basics", "summary", "profiles"],
    },
  },
  mudkip: {
    name: "mudkip",
    displayName: "Fresh Perspective",
    columnType: "single",
    enabled: { resume: true, cover: false },
    features: {
      themeSupport: false,
      photoSupport: true,
      layoutCustomization: true,
      borderRadius: true,
      spacing: true,
    },
    layout: {
      mainSections: [
        "experience",
        "education",
        "skills",
        "projects",
        "languages",
        "awards",
        "certifications",
        "interests",
        "volunteer",
        "references",
        "publications",
      ],
      sidebarSections: [],
      fixedSections: ["basics", "summary", "profiles"],
    },
  },
  arcanine: {
    name: "arcanine",
    displayName: "Premium Select",
    columnType: "multi",
    enabled: { resume: true, cover: false },
    features: {
      themeSupport: true,
      photoSupport: true,
      layoutCustomization: true,
      borderRadius: true,
      spacing: true,
    },
    layout: {
      mainSections: ["experience", "education", "projects", "volunteer"],
      sidebarSections: [
        "skills",
        "languages",
        "awards",
        "certifications",
        "interests",
        "references",
        "publications",
      ],
      fixedSections: ["basics", "summary", "profiles"],
    },
  },
  flareon: {
    name: "flareon",
    displayName: "Elegant Accent",
    columnType: "single",
    enabled: { resume: true, cover: false },
    features: {
      themeSupport: false,
      photoSupport: true,
      layoutCustomization: true,
      borderRadius: true,
      spacing: true,
    },
    layout: {
      mainSections: [
        "experience",
        "education",
        "skills",
        "projects",
        "languages",
        "awards",
        "certifications",
        "interests",
        "volunteer",
        "references",
        "publications",
      ],
      sidebarSections: [],
      fixedSections: ["basics", "summary", "profiles"],
    },
  },
  glaceon: {
    name: "glaceon",
    displayName: "Ice Crystal",
    columnType: "single",
    enabled: { resume: true, cover: false },
    features: {
      themeSupport: true,
      photoSupport: true,
      layoutCustomization: true,
      borderRadius: true,
      spacing: true,
    },
    layout: {
      mainSections: [
        "experience",
        "education",
        "skills",
        "projects",
        "languages",
        "awards",
        "certifications",
        "interests",
        "volunteer",
        "references",
        "publications",
      ],
      sidebarSections: [],
      fixedSections: ["basics", "summary", "profiles"],
    },
  },
  espeon: {
    name: "espeon",
    displayName: "Mystic Purple",
    columnType: "multi",
    enabled: { resume: true, cover: false },
    features: {
      themeSupport: true,
      photoSupport: true,
      layoutCustomization: true,
      borderRadius: true,
      spacing: true,
    },
    layout: {
      mainSections: ["experience", "education", "projects", "volunteer"],
      sidebarSections: [
        "skills",
        "languages",
        "awards",
        "certifications",
        "interests",
        "references",
        "publications",
      ],
      fixedSections: ["basics", "summary", "profiles"],
    },
  },
};

// Helper functions
export const getTemplateMetadata = (template: Template): TemplateMetadata => {
  return templateMetadata[template];
};

export const isTemplateFeatureEnabled = (
  template: Template,
  feature: keyof TemplateMetadata["features"],
): boolean => {
  return templateMetadata[template].features[feature];
};

export const getTemplateColumnType = (template: Template): "single" | "multi" => {
  return templateMetadata[template].columnType;
};

export const getTemplateMainSections = (template: Template): string[] => {
  return templateMetadata[template].layout.mainSections;
};

export const getTemplateSidebarSections = (template: Template): string[] => {
  return templateMetadata[template].layout.sidebarSections;
};

export const getTemplateFixedSections = (template: Template): string[] => {
  return templateMetadata[template].layout.fixedSections;
};

export const isSectionMovable = (template: Template, sectionId: string): boolean => {
  const fixedSections = getTemplateFixedSections(template);
  return !fixedSections.includes(sectionId);
};

export const isTemplateEnabled = (
  template: Template,
  documentType: "resume" | "cover",
): boolean => {
  const metadata = getTemplateMetadata(template);
  const { enabled } = metadata;

  if (typeof enabled === "boolean") {
    return enabled;
  }

  return enabled[documentType];
};

export const getEnabledTemplates = (documentType: "resume" | "cover"): Template[] => {
  return (Object.keys(templateMetadata) as Template[]).filter((template) =>
    isTemplateEnabled(template, documentType),
  );
};

export const getDisabledTemplates = (documentType: "resume" | "cover"): Template[] => {
  return (Object.keys(templateMetadata) as Template[]).filter(
    (template) => !isTemplateEnabled(template, documentType),
  );
};
